// SSCC barcode normaliser.
//
// USB barcode scanners with GS1-128 emulation prepend an AIM symbology
// identifier `]C1` (or `]C0` for non-GS1 Code 128) to indicate the
// barcode kind, then the GS1 Application Identifier `00` (SSCC) before
// the 17/18-digit SSCC payload. The raw string a scanner sends in
// scanner-mode looks like:
//
//   ]C10066099004423016754
//   │ │ │  └────── 18-digit SSCC payload
//   │ │ └── AI 00 = SSCC
//   │ └── ']C1' AIM Symbology Identifier (GS1-128)
//   └── ASCII close-bracket
//
// We strip both prefixes client-side for immediate display feedback
// (the user sees the clean SSCC the instant they scan, not after the
// save round-trip). The set_pallet_ssc RPC (mig 577) re-runs the same
// strip server-side so any code path that bypasses this util — paste,
// auto-fill, future scanner page — still saves clean values.
//
// Edge cases:
//   - Bare 18-digit SSCC manually typed: returned as-is.
//   - Bare 18-digit SSCC starting with `00`: returned as-is (we only
//     strip leading `00` when the total length matches an AI-prefixed
//     19/20-digit value).
//   - Empty / whitespace-only input: returns empty string.
//   - Non-digit chars (FNC1 control bytes, hyphens, spaces): stripped.

export function normaliseSscc(raw: string | null | undefined): string {
  if (!raw) return ''
  let v = raw.trim()
  // Strip GS1-128 AIM symbology identifier ']C0' / ']C1' (case-insensitive).
  v = v.replace(/^\]C[01]/i, '')
  // Drop non-digits (FNC1, hyphens, the parentheses in a printed "(00)…").
  //
  // ⚠️ This runs BEFORE the AI strip below, and the order is load-bearing. It
  // used to run after, which meant a value pasted from a printed
  // human-readable line — "(00)600123456000000018", 22 characters — never
  // matched the 19/20 length test, so its AI survived and a 20-digit value was
  // stored. That was masked because set_pallet_ssc re-ran the same strip on the
  // client's output and cancelled it; nothing masks it for a caller that writes
  // the column directly, which is what the pallets trigger (mig 20260821000000)
  // now backstops.
  v = v.replace(/[^0-9]/g, '')
  // Strip a leading SSCC AI '00' only at the expected 19/20-digit-with-AI
  // length. Prevents a genuine bare 18-digit SSCC that happens to start '00'
  // from being truncated.
  if ((v.length === 19 || v.length === 20) && v.startsWith('00')) {
    v = v.slice(2)
  }
  return v
}

// GS1 mod-10 check digit over a 17-digit SSCC body (the first 17 of the 18
// digits). Rightmost data digit is weighted ×3, alternating ×1/×3 leftward —
// for 17 digits the leftmost (index 0) is ×3. Mirrors sscc_check_digit() in
// migration 889 so client validation matches what the mint RPC produces.
export function ssccCheckDigit(body17: string): number {
  if (!/^\d{17}$/.test(body17)) {
    throw new Error(`ssccCheckDigit expects 17 digits, got "${body17}"`)
  }
  let sum = 0
  for (let i = 0; i < 17; i++) {
    sum += Number(body17[i]) * (i % 2 === 0 ? 3 : 1)
  }
  return (10 - (sum % 10)) % 10
}

// True when a normalised value is a structurally-valid 18-digit SSCC: 18 digits
// and the 18th is the correct GS1 check digit over the first 17. Use to flag a
// mistyped/short externally-entered code before it's printed as a GS1-128.
/**
 * Operator-facing warning for a typed/scanned SSCC, or null when there is
 * nothing to say. Soft validation only — per the 2026-08-17 decision this
 * NEVER refuses a save and never silently rewrites the value: a check digit
 * that fails is far more often a legacy code than a typo, and quietly
 * downgrading it would make a good-but-odd code start printing as something
 * else with nobody told.
 */
export function ssccWarning(raw: string | null | undefined): string | null {
  const v = normaliseSscc(raw)
  if (!v) return null
  if (v.length !== 18) {
    return `An SSCC is 18 digits — this is ${v.length}. It will be saved as typed, but it won't scan as a GS1 code.`
  }
  return isValidSscc(v)
    ? null
    : 'The check digit does not match the rest of this SSCC — usually a mistyped digit. It will be saved as typed; re-scan to be sure.'
}

export function isValidSscc(raw: string | null | undefined): boolean {
  const v = normaliseSscc(raw)
  if (!/^\d{18}$/.test(v)) return false
  return ssccCheckDigit(v.slice(0, 17)) === Number(v[17])
}
